import React, { CSSProperties } from 'react';
import Modal from 'components/modal';
import { withForwardRef } from 'hoc';
import { useSetState } from 'hooks';
import { isDefined } from 'utils/js';
import { noop } from 'lodash';

export interface WithModalProps {
  isOpen?: boolean;
  ref?: React.Ref<HTMLElement>;
  forwardedRef?: React.Ref<HTMLElement>;
  onHide: Function;
  onShow: Function;
  onBeforeHide: Function;
  onBeforeShow: Function;
  autoHideAfter: number;
  modal: ModalContext;
  backgroundColor: string;
  width?: string;
  style?: ModalStyles;
  className?: string;
}

interface ModalStyles {
  content?: CSSProperties;
  overlay?: CSSProperties;
}

export interface ModalContext {
  show: Function;
  hide: Function;
  data: any;
  setData: Function;
}

const defaultProps = {
  isOpen: false,
  autoHideAfter: undefined,
  onHide: noop,
  onShow: noop,
  onBeforeHide: noop,
  onBeforeShow: noop,
  style: undefined,
  forwardRef: undefined,
};

/**
 * withModal hoc is a function that creates and returns modal components. Under the hood withModal
 * hoc uses our portal implementation which can be found here src/react/components/portal/index.js:4
 * ModalRendererComponent is used if we want to override the default modal component used which is
 * Modal component from react native.
 *
 * @param ContentComponent
 * @param ModalRendererComponent
 * @returns {React.ComponentType<{} & React.ClassAttributes<any>>}
 */
function withModal<Props extends WithModalProps = WithModalProps>(
  ContentComponent,
  ModalRendererComponent = Modal,
) {
  function ModalComponent({
    forwardedRef,
    className,
    ...props
  }: WithModalProps) {
    const initialState = {
      isOpen: props.isOpen,
      data: {},
    };

    const [state, setState] = useSetState(initialState);

    let autoHideTimeoutHandle;

    React.useEffect(() => {
      setState((state) => ({ ...state, isOpen: props.isOpen }));
    }, [props.isOpen]);

    React.useEffect(() => {
      return () => {
        if (isDefined(props.autoHideAfter)) {
          clearTimeout(autoHideTimeoutHandle);
        }
      };
    });

    React.useImperativeHandle(forwardedRef, (): any => ({
      show,
      hide,
      setData,
      isOpen,
    }));

    function autoHide() {
      const { autoHideAfter } = props;
      if (isDefined(autoHideAfter)) {
        autoHideTimeoutHandle = setTimeout(hide, autoHideAfter);
      }
    }

    function show() {
      return Promise.resolve(props.onBeforeShow()).then(() => {
        setState({ isOpen: true });
        onShow();
        autoHide();
      });
    }

    function hide() {
      return Promise.resolve(props.onBeforeHide()).then(() => {
        setState({ isOpen: false });
        onHide();
      });
    }

    function onHide() {
      props.onHide(state);
    }

    function onShow() {
      props.onShow(state);
    }

    function isOpen() {
      return state.isOpen;
    }

    function setData(data) {
      setState({ data });

      return { show, hide };
    }

    const { modal } = props;

    const modalProperties = {
      ...modal,
      isOpen: state.isOpen,
      onShow,
      onHide,
      onRequestClose: onHide,
      style: props.style,
      className,
    };

    const modalContext = {
      show,
      hide,
      isOpen: state.isOpen,
      setData,
    };

    return (
      <ModalRendererComponent {...modalProperties} modal={modalContext}>
        <ContentComponent {...props} modal={modalContext} data={state.data} />
      </ModalRendererComponent>
    );
  }

  ModalComponent.defaultProps = defaultProps;

  return withForwardRef<HTMLElement, Props>(ModalComponent);
}

export default withModal;
